/**
 * rootResolution.ts — the pure decision core of the state-aware root.
 *
 * Strangers see the story; members see the app; nobody is shown the wrong thing, ever.
 * Extracted as a pure function so the decision matrix is unit-testable without React: the
 * "no flash" requirement lives or dies on this logic running synchronously before first
 * paint, and a table this consequential should be assertable line by line.
 *
 * The fail-toward-story rule (hard requirement): every ambiguous or error state — expired
 * session, failed auth check, network error, resolution timeout — lands on the STORY page
 * with sign-in offered. A journalist must never hit a login wall; a member must never hit a
 * manifesto. When signals conflict, the story with a prominent sign-in degrades correctly
 * for both.
 */

/** Set at every successful sign-in, cleared on explicit sign-out. */
export const SIGNED_IN_MARKER = "zl_has_signed_in";

export function readSignedInMarker(): boolean {
  try {
    return localStorage.getItem(SIGNED_IN_MARKER) === "1";
  } catch {
    // Storage unavailable (private mode) — treat as a stranger; the story degrades correctly.
    return false;
  }
}

export function writeSignedInMarker(): void {
  try {
    localStorage.setItem(SIGNED_IN_MARKER, "1");
  } catch {
    /* non-fatal */
  }
}

export function clearSignedInMarker(): void {
  try {
    localStorage.removeItem(SIGNED_IN_MARKER);
  } catch {
    /* non-fatal */
  }
}

export type RootOutcome =
  | { kind: "splash" } // returning-member signal present, auth still resolving — hold paint
  | { kind: "app" } // valid session — route to the member app, zero story frames
  | { kind: "story"; promoteSignIn: boolean }; // everyone else

export function decideRoot(input: {
  hasMarker: boolean;
  authLoading: boolean;
  signedIn: boolean;
  timedOut: boolean;
}): RootOutcome {
  // A resolved session wins regardless of markers — a member never sees marketing.
  if (input.signedIn) return { kind: "app" };

  if (input.hasMarker) {
    // Returning-member signal: hold on the brand splash while the session check runs —
    // unless it has run too long, in which case fail toward the story (never a spinner
    // trap, never a bare login form).
    if (input.authLoading && !input.timedOut) return { kind: "splash" };
    // Marker but no session: expired. Story, with "Continue to sign in" promoted.
    return { kind: "story", promoteSignIn: true };
  }

  // No marker: a stranger. Story immediately — do not hold their paint hostage to an auth
  // check that is almost certainly going to resolve to nobody. (The one edge — restorable
  // session with cleared marker — self-corrects: when auth resolves, the resolver routes
  // to the app.)
  return { kind: "story", promoteSignIn: false };
}
